import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

export interface ApiError {
  error: string;
}

export interface DemoSystem {
  id: string;
  name: string;
  // Which provisioner backend owns this system ('local-podman' | 'aws-cfn' | 'ml-lab'). Drives
  // backend-specific affordances — e.g. the console-session (shell) button is local-podman only.
  backend?: string;
  type: string;
  state: string;
  publicIp: string | null;
  consoleUrl: string | null;
  s3ApiUrl: string | null;
  // Extra endpoints for a capabilities-lab system (peer + tier Console, Grafana, …); null otherwise.
  serviceUrls?: { console2?: string; console3?: string; grafana?: string; prometheus?: string } | null;
  // Copy-paste S3 endpoints for a local `mc` client (capabilities lab only) — each cluster plus the
  // failover load balancer. Rendered as `mc alias set …` in the admin panel + `mc alias rm …` on
  // destroy. Localhost URLs when private, public tunnel hosts when exposed. Null otherwise.
  mcAliases?: { name: string; url: string; role: string }[] | null;
  nodeCount: number;
  drivesPerNode?: number; // >1 for a local custom SNMD/distributed shape
  capacityGiB?: number | null; // advisory per-drive capacity (local custom)
  licenseTier: string;
  arch: string | null;
  pattern: string | null;
  stackName: string | null;
  // SSO systems are fronted by a tunnel (inbound closed); hostname is the stable Console host.
  hostname: string | null;
  s3Host: string | null; // stable S3-API tunnel host (exposed systems) — Databricks-reachable
  oidcEnabled: boolean;
  // Server-computed: whether this system can be torn down from the dashboard (parity across backends).
  deletable: boolean;
}

export interface CatalogPattern {
  id: string;
  label: string;
  description: string;
  nodeCount: number;
  licenseTier: string;
  arch: string;
  instanceType: string;
  dataVolumeGiB: number;
  estCostMonthlyRunning: number;
  estCostMonthlyStopped: number;
  concurrencyCap: number;
  ttlHours: number;
  activeCount: number;
  remaining: number;
  // Which provisioner backends can serve this pattern (drives which launch fields are shown).
  backends: string[];
  // 'custom' patterns let the launcher choose the shape (nodes/drives/capacity) within `custom` bounds.
  shapeKind?: 'fixed' | 'custom';
  custom?: {
    minNodes: number;
    maxNodes: number;
    defaultNodes: number;
    minDrivesPerNode: number;
    maxDrivesPerNode: number;
    defaultDrivesPerNode: number;
    defaultCapacityGiB: number;
    maxCapacityGiB: number;
  };
}

// ---- Demos layer ----
export interface WalkthroughAction {
  kind: 'setup' | 'run-step' | 'publish-share' | 'databricks-count' | 'databricks-setup' | 'databricks-teardown' | 'share-teardown' | 'mc-script' | 'runner';
  step?: string;
  table?: string;
  expect?: number;
}
export type WalkthroughPhase =
  | 'BOOTH'
  | 'AISTOR'
  | 'DATABRICKS'
  | 'IAM'
  | 'ACCESS'
  | 'TIERING'
  | 'REPLICATION'
  | 'QOS'
  | 'GOVERNANCE'
  | 'OBSERVABILITY';
export interface WalkthroughStep {
  id: string;
  title: string;
  phase: WalkthroughPhase;
  narrativeHtml: string;
  action?: WalkthroughAction;
  // Auto-retry hint for verify-style steps (ILM scanner pass, async replication): on a not-yet-met
  // `expect`, the walkthrough re-runs the step every intervalSeconds (visible countdown), up to
  // maxAttempts total runs.
  poll?: { intervalSeconds: number; maxAttempts: number; waitingMessage: string };
}
export type IntegrationId = 'databricks';
export interface DemoDescriptor {
  id: string;
  label: string;
  description: string;
  lane: string;
  requires: { tables?: boolean; deltaSharing?: boolean; kms?: boolean; tiering?: boolean; replication?: boolean; observability?: boolean };
  requiresIntegrations: IntegrationId[];
  walkthrough: WalkthroughStep[];
  teardown: WalkthroughStep[];
}
export interface Check {
  id: string;
  label: string;
  ok: boolean;
  detail: string;
}
export interface DemoStatus {
  demoId: string;
  systemId: string;
  attached: boolean;
  available: 'available' | 'needs-setup' | 'unavailable';
  checks: Check[];
  sharePublished: boolean;
  warehouse: string;
  databricksUrl: string | null;
  databricksCatalog: string | null;
  databricksProvider: string | null;
  databricksSchema: string;
}
export interface StepResult {
  stepId: string;
  ok: boolean;
  rows: number | null;
  message: string;
  commands: string[];
  output: string;
}

async function request<T>(path: string, method: 'GET' | 'POST' | 'DELETE', body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error((data as ApiError).error ?? `HTTP ${res.status}`);
  return data as T;
}

export const api = {
  me: () => request<{ email: string; displayName: string }>('/api/auth/me', 'GET'),
  passwordLogin: (email: string, password: string) =>
    request<{ next: 'enroll'; email: string; displayName: string }>('/api/auth/login', 'POST', {
      email,
      password,
    }),
  registerBegin: () =>
    request<PublicKeyCredentialCreationOptionsJSON>('/api/auth/passkey/register/begin', 'POST'),
  registerFinish: (response: unknown) =>
    request<{ ok: true }>('/api/auth/passkey/register/finish', 'POST', response),
  loginBegin: (email: string) =>
    request<PublicKeyCredentialRequestOptionsJSON>('/api/auth/passkey/login/begin', 'POST', { email }),
  loginFinish: (response: unknown) =>
    request<{ ok: true }>('/api/auth/passkey/login/finish', 'POST', response),
  logout: () => request<{ ok: true }>('/api/auth/logout', 'POST'),
  systems: () => request<{ systems: DemoSystem[] }>('/api/systems', 'GET'),
  startSystem: (id: string) => request<{ ok: true }>(`/api/systems/${id}/start`, 'POST'),
  stopSystem: (id: string) => request<{ ok: true }>(`/api/systems/${id}/stop`, 'POST'),
  adminLogin: (id: string) =>
    request<{
      username: string;
      password: string;
      consoleUrl: string | null;
      grafana?: { username: string; password: string; url: string | null };
    }>(
      `/api/systems/${id}/admin`,
      'GET',
    ),
  generateKey: (id: string) =>
    request<{ accessKey: string; secretKey: string }>(`/api/systems/${id}/keys`, 'POST'),
  // Containers of a system (primary + peers/nodes/sidecars) — the console-session node switcher.
  systemContainers: (id: string) =>
    request<{ containers: { name: string; role: string; running: boolean }[] }>(
      `/api/systems/${id}/containers`,
      'GET',
    ),
  catalog: () => request<{ patterns: CatalogPattern[] }>('/api/catalog', 'GET'),
  deploySystem: (
    patternId: string,
    opts?: {
      name?: string;
      allowedCidr?: string;
      expose?: boolean;
      sso?: boolean;
      nodeCount?: number;
      drivesPerNode?: number;
      capacityGiB?: number;
    },
  ) => request<{ stackName: string | null; systemName: string; id: string }>('/api/systems', 'POST', { patternId, ...opts }),
  destroySystem: (id: string) => request<{ ok: true }>(`/api/systems/${id}`, 'DELETE'),
  // ---- demos ----
  demos: () => request<{ demos: DemoDescriptor[] }>('/api/demos', 'GET'),
  systemDemos: (id: string) => request<{ statuses: DemoStatus[] }>(`/api/systems/${id}/demos`, 'GET'),
  attachDemo: (id: string, demoId: string, opts?: { databricksCatalog?: string; warehouse?: string }) =>
    request<{ attachment: unknown }>(`/api/systems/${id}/demos/${demoId}/attach`, 'POST', opts ?? {}),
  databricksConfig: () => request<{ host: string; configured: boolean }>('/api/databricks/config', 'GET'),
  setDatabricksConfig: (host: string, token: string) =>
    request<{ ok: true }>('/api/databricks/config', 'POST', { host, token }),
  clearDatabricksConfig: () => request<{ ok: true }>('/api/databricks/config', 'DELETE'),
  detachDemo: (id: string, demoId: string) =>
    request<{ ok: true }>(`/api/systems/${id}/demos/${demoId}`, 'DELETE'),
  validateDemo: (id: string, demoId: string) =>
    request<{ status: DemoStatus }>(`/api/systems/${id}/demos/${demoId}/validate`, 'POST'),
  runStep: (id: string, demoId: string, stepId: string) =>
    request<{ result: StepResult }>(`/api/systems/${id}/demos/${demoId}/steps/${stepId}/run`, 'POST'),
};
